import React, { useMemo, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import { useSidebarSiteQuery, SidebarSiteQuery } from '@/.generated/graphql';
import { useSpaceAccess } from '@/contexts/SpaceAccessContext';

/** Common item shape passed to antd Menu / Collapse. The trees build their
 * own labels per module — this is just the antd-compatible structure. */
export type MenuItem = {
  key: string;
  label: string | React.ReactNode;
  icon?: React.ReactNode;
  children?: MenuItem[];
  className?: string;
};

/** Raw access-filtered site tree shared by every module. The trees consume
 * this and build their own module-specific antd Menu items. */
export type RawSite = NonNullable<SidebarSiteQuery['sites']>['data'][number];
export type RawBuilding = NonNullable<RawSite['buildings']>[number];
export type RawFloor = NonNullable<RawBuilding['floors']>[number];
export type RawRoom = NonNullable<RawFloor['rooms']>[number];
export type RawZone = NonNullable<RawFloor['zones']>[number];

/** Combined room-or-zone (matches the legacy `roomszones` field shape — rooms
 * with their nested zones, plus standalone floor zones). */
export type RoomZoneItem =
  | (RawRoom & { kind: 'room'; zones: RawZone[] })
  | (RawZone & { kind: 'zone' });

export type FilteredFloor = Omit<RawFloor, 'rooms' | 'zones'> & {
  roomszones: RoomZoneItem[];
};
export type FilteredBuilding = Omit<RawBuilding, 'floors'> & {
  floors: FilteredFloor[];
};
export type FilteredSite = Omit<RawSite, 'buildings'> & {
  buildings: FilteredBuilding[];
};

export const MENU_STYLE = { borderRight: '0px' } as const;

/** Shared antd theme for the Monitor / Studio / Devices Spaces trees.
 * Hoisted to a module-level constant so the object identity is stable
 * across renders — otherwise antd's ConfigProvider re-derives Menu
 * tokens on every re-render of the tree. */
export const SIDEBAR_TREE_THEME = {
  components: {
    Menu: {
      itemBorderRadius: 9999,
      itemMarginBlock: 2,
      itemHeight: 36,
    },
  },
} as const;

/** Sentinel key for the "All Spaces" selectable item in Devices mode. */
export const ALL_SPACES_KEY = '__all_spaces__';

const naturalSort = (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });

/** Wraps a menu-item row in a Tooltip that anchors to the pill and appears
 * fully outside the pill to the right (clearing the row's hover-only edit
 * controls). The tooltip only opens if the inner content is actually
 * truncated — measured on hover via scrollWidth > clientWidth. */
export const EllipsisRowTooltip: React.FC<{ name: string; children: React.ReactNode }> = ({
  name,
  children,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [offsetX, setOffsetX] = useState(24);
  return (
    <Tooltip
      title={name}
      placement="right"
      arrow={false}
      open={open}
      align={{ offset: [offsetX, 0] }}
      onOpenChange={(visible) => {
        if (!visible) {
          setOpen(false);
          return;
        }
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        // Only measure the actual label nodes — Typography.Text with
        // `ellipsis` renders `.ant-typography-ellipsis`. Layout wrappers
        // like `.overflow-hidden` are not the visible label and their
        // scrollWidth/clientWidth ratio reflects flex sizing, not
        // truncation, which would produce false-positive tooltips on
        // rows whose name actually fits.
        const candidates = Array.from(
          wrapper.querySelectorAll<HTMLElement>('.ant-typography-ellipsis'),
        );
        const truncated = candidates.some((el) => el.scrollWidth > el.clientWidth);
        if (!truncated) {
          setOpen(false);
          return;
        }
        const pill = wrapper.closest<HTMLElement>(
          '.ant-menu-item, .ant-menu-submenu-title, .ant-collapse-header',
        );
        if (pill) {
          const gap = pill.getBoundingClientRect().right - wrapper.getBoundingClientRect().right;
          setOffsetX(Math.max(0, gap) + 8);
        }
        setOpen(true);
      }}
    >
      <div ref={wrapperRef} style={{ width: '100%' }}>
        {children}
      </div>
    </Tooltip>
  );
};

/** Recursively trim a subtree so only the branch leading to selectedKey
 * survives. Used by Monitor (Site-level chain) and Studio (Building-level
 * chain). Returns null if selectedKey isn't under this node. */
export function trimToChain(node: MenuItem, selectedKey: string | null): MenuItem | null {
  if (!selectedKey) return null;
  if (node.key === selectedKey) return node;
  if (!node.children) return null;
  for (const child of node.children) {
    const trimmed = trimToChain(child, selectedKey);
    if (trimmed) return { ...node, children: [trimmed] };
  }
  return null;
}

/** Walk the tree and tag the item whose key === selectedKey with the
 * `sidebar-item-directly-selected` class so CSS can draw the selected pill
 * on it (covers leaf Menu.Items AND SubMenu titles — antd's native
 * itemSelectedBg only handles leaves). */
export function markSelected(
  items: MenuItem[] | undefined,
  selectedKey: string | null,
): MenuItem[] | undefined {
  if (!items || !selectedKey) return items;
  return items.map((item) => ({
    ...item,
    className: item.key === selectedKey ? 'sidebar-item-directly-selected' : item.className,
    children: item.children ? markSelected(item.children, selectedKey) : item.children,
  }));
}

/**
 * Spread this on a MenuItem definition to set `children` only when the
 * array is non-empty. antd Menu draws a chevron (and treats the row as
 * a SubMenu) whenever `children` is defined — even an empty `[]` —
 * which incorrectly suggests the row is expandable when in fact there
 * is nothing inside. Calling `...maybeChildren(arr)` makes the row
 * render as a leaf when `arr` is empty.
 */
export function maybeChildren(children: MenuItem[]): { children?: MenuItem[] } {
  return children.length > 0 ? { children } : {};
}

/** Collect every key in this MenuItem's subtree (the item itself + all
 * descendants). Used to filter the global `openKeys` down to a single
 * Site's subtree when each Site is rendered as its own antd Menu. */
export function collectAllKeys(item: MenuItem, into: Set<string> = new Set()): Set<string> {
  into.add(String(item.key));
  for (const child of item.children ?? []) collectAllKeys(child, into);
  return into;
}

/** Walk a trimmed `trimToChain` subtree and return every non-leaf key
 * EXCEPT `selectedKey`. Used to force the chain Menu fully open down to
 * (but not into) the selected item, so the breadcrumb path is visible
 * at a glance without expanding the selected leaf's own children. */
export function collectChainOpenKeys(item: MenuItem, selectedKey: string): string[] {
  const out: string[] = [];
  const walk = (node: MenuItem) => {
    if (!node.children || node.children.length === 0) return;
    if (node.key === selectedKey) return;
    out.push(String(node.key));
    for (const child of node.children) walk(child);
  };
  walk(item);
  return out;
}

/**
 * The "Show all floors" button hides only when the tree is exactly in the
 * canonical all-floors view: every Site and every Building expanded, and
 * NO Floor expanded. Anywhere else (a Site collapsed, a Building collapsed,
 * or a Floor expanded showing rooms/zones) the button stays visible so the
 * user always has a one-click way back to the flat all-floors layout.
 */
export function isInAllFloorsView(items: MenuItem[], openKeys: string[]): boolean {
  const open = new Set(openKeys);
  for (const site of items) {
    if (!open.has(String(site.key))) return false;
    for (const building of site.children ?? []) {
      if (!open.has(String(building.key))) return false;
      for (const floor of building.children ?? []) {
        if (open.has(String(floor.key))) return false;
      }
    }
  }
  return true;
}

/** Cheap structural fingerprint of the raw site tree. Walks only the fields
 * the filter pipeline and downstream consumers actually read (id, name, and
 * zone.roomId for the room/zone grouping). Replaces a full `JSON.stringify`
 * of the tree, which was serializing every nested object's fields on every
 * Apollo cache write — for an org with thousands of leaf nodes that shows
 * up on the render path of every page mounting the sidebar. */
function sitesFingerprint(sites: RawSite[] | undefined): string {
  if (!sites) return '';
  const parts: string[] = [];
  for (const s of sites) {
    parts.push('s', s.id, s.name ?? '');
    for (const b of s.buildings ?? []) {
      parts.push('b', b.id, b.name ?? '');
      for (const f of b.floors ?? []) {
        parts.push('f', f.id, f.name ?? '');
        for (const r of f.rooms ?? []) {
          parts.push('r', r.id, r.name ?? '');
        }
        for (const z of f.zones ?? []) {
          parts.push('z', z.id, z.name ?? '', z.roomId ?? '');
        }
      }
    }
  }
  return parts.join('|');
}

/** Hook that fetches the user's site tree, filters by access, and returns
 * a stable reference (only changes when the actual data changes — Apollo
 * cache writes to floors/buildings during tab switches won't churn this). */
export function useFilteredSites(filterValue: string): {
  sites: FilteredSite[] | undefined;
  error: unknown;
} {
  const { data: rawSiteData, error: siteError } = useSidebarSiteQuery({
    fetchPolicy: 'cache-and-network',
  });
  const rawSites = rawSiteData?.sites?.data;
  const fingerprint = useMemo(() => sitesFingerprint(rawSites), [rawSites]);
  const prevFingerprintRef = useRef(fingerprint);
  const stableSitesRef = useRef(rawSites);
  if (fingerprint !== prevFingerprintRef.current) {
    prevFingerprintRef.current = fingerprint;
    stableSitesRef.current = rawSites;
  }

  // Only `hasAccess` is read in the memo body — destructuring `assignments`
  // / `isUnrestricted` here would force them into the memo's dep array and
  // re-run the entire filter pipeline on every SpaceAccessContext publish.
  const { hasAccess } = useSpaceAccess();

  const filteredSites = useMemo<FilteredSite[] | undefined>(() => {
    const isZoneAccessible = (zoneId: string) => hasAccess('zone', zoneId);
    const isRoomAccessible = (roomId: string) => hasAccess('room', roomId);
    const isFloorAccessible = (floorId: string) => hasAccess('floor', floorId);
    const isBuildingAccessible = (buildingId: string) => hasAccess('building', buildingId);

    const isFloorOrChildrenAccessible = (
      floorId: string,
      rooms: readonly { id: string }[] | null | undefined,
      zones: readonly { id: string }[] | null | undefined,
    ) => {
      if (isFloorAccessible(floorId)) return true;
      return (
        (rooms || []).some((r) => isRoomAccessible(r.id)) ||
        (zones || []).some((z) => isZoneAccessible(z.id))
      );
    };

    const isBuildingOrChildrenAccessible = (
      buildingId: string,
      floors:
        | readonly {
            id: string;
            rooms?: readonly { id: string }[] | null;
            zones?: readonly { id: string }[] | null;
          }[]
        | null
        | undefined,
    ) => {
      if (isBuildingAccessible(buildingId)) return true;
      return (floors || []).some(
        (f) =>
          isFloorAccessible(f.id) ||
          (f.rooms || []).some((r) => isRoomAccessible(r.id)) ||
          (f.zones || []).some((z) => isZoneAccessible(z.id)),
      );
    };

    const isSiteOrChildrenAccessible = (
      siteId: string,
      buildings:
        | readonly {
            id: string;
            floors?:
              | readonly {
                  id: string;
                  rooms?: readonly { id: string }[] | null;
                  zones?: readonly { id: string }[] | null;
                }[]
              | null;
          }[]
        | null
        | undefined,
    ) => {
      if (hasAccess('site', siteId)) return true;
      return (buildings || []).some((b) => isBuildingOrChildrenAccessible(b.id, b.floors));
    };

    const allSites = stableSitesRef.current || [];
    const sites = allSites.filter((site) =>
      isSiteOrChildrenAccessible(site.id, site.buildings || []),
    );

    // Hoist once at the top of the memo body so we don't recompute the same
    // lowered string at every floor / building / site filter step inside
    // hot inner loops.
    const filterLower = filterValue.toLowerCase();

    return sites
      .map<FilteredSite>((site) => ({
        ...site,
        buildings: (site.buildings ?? [])
          .filter((b) => isBuildingOrChildrenAccessible(b.id, b.floors || []))
          .map<FilteredBuilding>((building) => ({
            ...building,
            floors: (building.floors ?? [])
              .filter((floor) =>
                isFloorOrChildrenAccessible(floor.id, floor.rooms || [], floor.zones || []),
              )
              .map<FilteredFloor>((floor) => {
                const roomItems = (floor.rooms ?? [])
                  .filter((r) => isRoomAccessible(r.id))
                  .map((room) => ({
                    ...room,
                    kind: 'room' as const,
                    zones: (floor.zones ?? [])
                      .filter((z) => z.roomId === room.id)
                      .filter((z) => isZoneAccessible(z.id)),
                  }))
                  .filter(
                    (room) =>
                      room.name?.toLowerCase().includes(filterLower) ||
                      room.zones.some((z) => z.name?.toLowerCase().includes(filterLower)),
                  );
                const zoneItems = (floor.zones ?? [])
                  .filter((z) => !z.roomId || !isRoomAccessible(z.roomId))
                  .filter((z) => isZoneAccessible(z.id))
                  .filter((z) => z.name?.toLowerCase().includes(filterLower))
                  .map((zone) => ({ ...zone, kind: 'zone' as const }));
                const combined: RoomZoneItem[] = [...roomItems, ...zoneItems];
                combined.sort(naturalSort);
                const { rooms: _r, zones: _z, ...rest } = floor;
                return { ...rest, roomszones: combined };
              })
              .filter((f) => f.name?.toLowerCase().includes(filterLower) || f.roomszones.length > 0)
              .sort(naturalSort),
          }))
          .filter((b) => b.name?.toLowerCase().includes(filterLower) || b.floors.length > 0)
          .sort(naturalSort),
      }))
      .filter((s) => s.name?.toLowerCase().includes(filterLower) || s.buildings.length > 0)
      .sort(naturalSort);
    // The body reads `stableSitesRef.current` (a ref) — refs aren't tracked
    // by exhaustive-deps. We keep `fingerprint` in the deps as the structural
    // invalidation signal (the ref-update gating block above mutates the ref
    // only when the fingerprint changes), even though the body doesn't read
    // `fingerprint` directly. eslint flags `fingerprint` as "unnecessary" in
    // that case — it isn't, structurally; disabling silences the false positive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint, filterValue, hasAccess]);

  return { sites: filteredSites, error: siteError };
}

/** Find the ancestor chain `[siteId, buildingId?, floorId?, ...]` for the
 * selected key. */
export function findAncestorChain(items: MenuItem[], selectedKey: string): string[] | null {
  const walk = (nodes: MenuItem[], chain: string[]): string[] | null => {
    for (const node of nodes) {
      if (node.key === selectedKey) return [...chain, node.key];
      if (node.children) {
        const found = walk(node.children, [...chain, node.key]);
        if (found) return found;
      }
    }
    return null;
  };
  return walk(items, []);
}
